import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';

// Icons per engine id — all use default portal blue scheme
const ENGINE_ICONS = {
  1:  'fas fa-clock',
  2:  'fas fa-user-check',
  5:  'fas fa-rotate-left',
  10: 'fas fa-heart-pulse',
  12: 'fas fa-bolt',
  14: 'fas fa-laptop',
  9:  'fas fa-leaf',
  7:  'fas fa-shield-halved',
  4:  'fas fa-infinity',
  17: 'fas fa-shield',
  13: 'fas fa-child-reaching',
  6:  'fas fa-car-burst',
  18: 'fas fa-sliders',
};

const ENGINE_GROUPS = [
  { label: 'Term',               ids: [1, 2, 5, 10, 12, 14] },
  { label: 'Final Expense / GI', ids: [9, 7] },
  { label: 'Permanent',          ids: [4, 17] },
  { label: 'Specialty',          ids: [6, 13] },
];

function InfoTooltip({ engineTitle }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '5px' }}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <i className="fas fa-circle-info" style={{ color: '#CBD5E1', fontSize: '12px', cursor: 'help' }} />
      {visible && (
        <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', background: '#0F172A', color: 'white', fontSize: '12px', lineHeight: '1.5', padding: '10px 14px', borderRadius: '8px', width: '240px', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', pointerEvents: 'none' }}>
          This link directs users to a quote funnel that exclusively quotes <strong style={{ color: '#93C5FD' }}>{engineTitle}</strong>. Prospects cannot switch to other insurance types from this URL.
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #0F172A' }} />
        </div>
      )}
    </span>
  );
}

function EngineCard({ engine, baseUrl }) {
  const [copied, setCopied] = useState(false);
  const url  = `${baseUrl}/quote/${engine.url_slug}`;
  const icon = ENGINE_ICONS[engine.id] || 'fas fa-link';

  const copy = () => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="portal-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', height: '74px', boxSizing: 'border-box' }}>

      {/* Icon */}
      <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={icon} style={{ color: '#2D87F0', fontSize: '20px' }}></i>
      </div>

      {/* Name + URL — same line, URL aligned */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', width: '280px', flexShrink: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{engine.title}</span>
          <InfoTooltip engineTitle={engine.title} />
        </div>
        <div
          onClick={copy}
          title={url}
          style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: '500', color: '#475569', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '7px', padding: '7px 12px', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em', transition: 'background 150ms' }}
          onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
          onMouseLeave={e => e.currentTarget.style.background = '#F8FAFC'}
        >
          <span style={{ color: '#94A3B8' }}>{baseUrl.replace(/https?:\/\//, '')}/quote/</span>
          <span style={{ color: '#2D87F0', fontWeight: '800' }}>{engine.url_slug}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        {/* Copy URL — same style as Preview */}
        <button onClick={copy}
          style={{ height: '40px', padding: '0 20px', background: copied ? '#16A34A' : 'white', color: copied ? 'white' : '#2D87F0', border: `2px solid ${copied ? '#16A34A' : '#2D87F0'}`, borderRadius: '9px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', transition: 'all 150ms', whiteSpace: 'nowrap' }}
          onMouseEnter={e => { if (!copied) e.currentTarget.style.background = '#EFF6FF'; }}
          onMouseLeave={e => { if (!copied) e.currentTarget.style.background = 'white'; }}>
          <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} style={{ fontSize: '14px' }}></i>
          {copied ? 'Copied!' : 'Copy URL'}
        </button>
        {/* Preview — blue outlined */}
        <a href={url} target="_blank" rel="noreferrer"
          style={{ height: '40px', padding: '0 20px', background: 'white', color: '#2D87F0', border: '2px solid #2D87F0', borderRadius: '9px', fontSize: '14px', fontWeight: '700', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '7px', transition: 'background 150ms', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
          onMouseLeave={e => e.currentTarget.style.background = 'white'}>
          <i className="fas fa-external-link-alt" style={{ fontSize: '14px' }}></i>
          Preview
        </a>
      </div>
    </div>
  );
}

export default function EngineUrlsPage() {
  const [enginesMap, setEnginesMap] = useState({});
  const [baseUrl, setBaseUrl]       = useState('https://demo.quoteplicity.com');
  const [loading, setLoading]       = useState(true);
  const [showVideo, setShowVideo]   = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => !!localStorage.getItem('qp_help_dismissed_engineurls')
  );

  useEffect(() => {
    api.get('/link-builder').then(res => {
      const map = {};
      (res.data.engines || []).forEach(e => { map[e.id] = e; });
      setEnginesMap(map);
      setBaseUrl(res.data.base_url || 'https://demo.quoteplicity.com');
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-link" style={{ color: '#2D87F0', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Engine URLs</h1>
        </div>

        {/* Banner */}
        {!bannerDismissed && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', padding: '14px 18px', background: 'white', border: '1px solid #DDE7F1', borderLeft: '4px solid #2D87F0', borderRadius: '10px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: '200px' }}>
              <i className="fas fa-graduation-cap" style={{ color: '#2D87F0', fontSize: '20px', marginTop: '2px', flexShrink: 0 }}></i>
              <div>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#0F172A', marginBottom: '2px' }}>Setup Guide: Engine URLs</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>Each card links directly to that insurance quote engine. Click the URL to copy it, or use Copy URL / Preview. Hover <i className="fas fa-circle-info" style={{ color: '#94A3B8' }}></i> for details on each engine.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => setShowVideo(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'white', background: showVideo ? '#1D6FD8' : '#2D87F0', border: 'none', cursor: 'pointer', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                <i className={`fas ${showVideo ? 'fa-times' : 'fa-play'}`} style={{ fontSize: '11px' }}></i>
                {showVideo ? 'Close Video' : 'Watch Video'}
              </button>
              <a href="https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/gVsESH1hovUPFZSIcI9b?preview=true" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2D87F0', background: '#EFF6FF', border: '1px solid #BFDBFE', textDecoration: 'none', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                <i className="fas fa-file-lines" style={{ fontSize: '11px' }}></i> Read Article
              </a>
              <button onClick={() => { localStorage.setItem('qp_help_dismissed_engineurls', '1'); setBannerDismissed(true); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', padding: '4px', lineHeight: 1 }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Video panel */}
        {showVideo && (
          <div className="portal-card" style={{ overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-play-circle" style={{ color: '#2D87F0', fontSize: '16px' }}></i>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>Setup Guide: Engine URLs</span>
              </div>
              <button onClick={() => setShowVideo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}><i className="fas fa-times"></i></button>
            </div>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              <iframe
                src="https://embed.app.guidde.com/playbooks/wV5eZEYMTdLpsf3h1SMPoG?mode=videoOnly"
                title="Manage Quote Engines and URLs in Quoteplicity Portal"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: '10px' }}
                referrerPolicy="unsafe-url"
                allow="clipboard-write"
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            {[...Array(9)].map((_, i) => <div key={i} className="skeleton" style={{ height: '160px', borderRadius: '12px' }}></div>)}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {ENGINE_GROUPS.map(group => {
              const groupEngines = group.ids.map(id => enginesMap[id]).filter(Boolean);
              if (!groupEngines.length) return null;
              return (
                <div key={group.label}>
                  {/* Group header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{group.label}</div>
                    <div style={{ flex: 1, height: '1px', background: '#DDE7F1' }}></div>
                    <span style={{ fontSize: '11px', color: '#94A3B8' }}>{groupEngines.length} engine{groupEngines.length > 1 ? 's' : ''}</span>
                  </div>

                  {/* Cards grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                    {groupEngines.map(engine => (
                      <EngineCard key={engine.id} engine={engine} baseUrl={baseUrl} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ProtectedLayout>
  );
}
